import { Alert, Button, Card, Col, Input, Row, Space, Tag, Typography } from 'antd';
import { useMemo, useRef, useState } from 'react';
import { PageHead } from '../components/PageHead';
import { wsUrl } from '../lib/config';

const { Title, Text, Paragraph } = Typography;

type LogEntry = {
  at: number;
  dir: 'in' | 'out';
  text: string;
};

const DEMO_NOTES = `# Demo notes

This is the simulator standing in for Keynote.

- Bullet one
- Bullet two

Replace this text and click **Push notes** to send it to the speaker's
browser. Tap → on the speaker's stage view to receive a "next" command
here; we'll auto-bump the slide number on the way back.
`;

export function SlideSimulatorScene() {
  const [key, setKey] = useState('');
  const [connected, setConnected] = useState(false);
  const [log, setLog] = useState<LogEntry[]>([]);
  const [notes, setNotes] = useState(DEMO_NOTES);
  const [slideNumber, setSlideNumber] = useState(1);
  const [totalSlides, setTotalSlides] = useState(18);
  const wsRef = useRef<WebSocket | null>(null);

  const url = useMemo(
    () => (key ? wsUrl(`/api/slides/${encodeURIComponent(key)}?role=app`) : null),
    [key],
  );

  const append = (entry: LogEntry) => {
    setLog((l) => [...l.slice(-49), entry]);
  };

  const sendNotes = (slide: number) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    const payload = {
      type: 'notes',
      text: notes,
      slideNumber: slide,
      totalSlides,
    };
    ws.send(JSON.stringify(payload));
    append({ at: Date.now(), dir: 'out', text: `notes (slide ${slide}/${totalSlides})` });
  };

  const connect = () => {
    if (!url) return;
    const ws = new WebSocket(url);
    wsRef.current = ws;
    ws.onopen = () => {
      setConnected(true);
      append({ at: Date.now(), dir: 'out', text: 'connected as role=app' });
      ws.send(
        JSON.stringify({ type: 'status', connected: true, appName: 'keynote' }),
      );
      sendNotes(slideNumber);
    };
    ws.onmessage = (ev) => {
      try {
        const msg = JSON.parse(ev.data as string) as { type: string };
        append({ at: Date.now(), dir: 'in', text: msg.type });
        if (msg.type === 'next') {
          setSlideNumber((n) => {
            const next = Math.min(n + 1, totalSlides);
            sendNotes(next);
            return next;
          });
        } else if (msg.type === 'prev') {
          setSlideNumber((n) => {
            const prev = Math.max(n - 1, 1);
            sendNotes(prev);
            return prev;
          });
        }
      } catch {
        // ignore
      }
    };
    ws.onclose = () => setConnected(false);
    ws.onerror = () => append({ at: Date.now(), dir: 'in', text: 'socket error' });
  };

  const disconnect = () => {
    wsRef.current?.close();
    wsRef.current = null;
    setConnected(false);
  };

  return (
    <div style={{ padding: 32, maxWidth: 960, margin: '0 auto' }}>
      <PageHead title="Slide control simulator" />
      <Title level={2}>Slide control simulator</Title>
      <Paragraph type="secondary">
        Stand-in for the AOTV Slide Control Mac app while it's being built.
        Paste a session key (from the team admin → Slide control panel for any
        speaker), tap <strong>Connect</strong>, and edit the notes on the right.
        Whatever you type gets streamed to the speaker's stage view in real time.
        Their <em>Next</em> / <em>Prev</em> buttons round-trip through this
        page just like the real Keynote bridge would.
      </Paragraph>

      <Row gutter={24}>
        <Col xs={24} md={10}>
          <Card title="Connection" size="small">
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              <Input
                placeholder="AOTV-XXXX-XXXX"
                value={key}
                onChange={(e) => setKey(e.target.value.trim())}
                disabled={connected}
                style={{ fontFamily: 'ui-monospace, Menlo, monospace' }}
              />
              {connected ? (
                <Button danger onClick={disconnect} block>
                  Disconnect
                </Button>
              ) : (
                <Button type="primary" onClick={connect} disabled={!key} block>
                  Connect
                </Button>
              )}
              <Space>
                <Text type="secondary">Status:</Text>
                <Tag color={connected ? 'green' : 'default'}>
                  {connected ? 'Connected' : 'Not connected'}
                </Tag>
              </Space>
            </Space>
          </Card>

          <Card title="Slide state" size="small" style={{ marginTop: 16 }}>
            <Space>
              <Text>Slide</Text>
              <Input
                type="number"
                value={slideNumber}
                onChange={(e) => setSlideNumber(Number(e.target.value) || 1)}
                style={{ width: 80 }}
              />
              <Text>of</Text>
              <Input
                type="number"
                value={totalSlides}
                onChange={(e) => setTotalSlides(Number(e.target.value) || 1)}
                style={{ width: 80 }}
              />
            </Space>
          </Card>

          <Card title="Event log" size="small" style={{ marginTop: 16 }}>
            <div
              style={{
                fontFamily: 'ui-monospace, Menlo, monospace',
                fontSize: 12,
                maxHeight: 200,
                overflowY: 'auto',
              }}
            >
              {log.length === 0 && <Text type="secondary">No events yet.</Text>}
              {log.slice().reverse().map((e, i) => (
                <div key={i}>
                  <Tag color={e.dir === 'in' ? 'blue' : 'green'}>
                    {e.dir === 'in' ? '↓' : '↑'}
                  </Tag>{' '}
                  {e.text}
                </div>
              ))}
            </div>
          </Card>
        </Col>

        <Col xs={24} md={14}>
          <Card title="Presenter notes (HTML allowed)" size="small">
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              <Input.TextArea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                autoSize={{ minRows: 14 }}
                style={{ fontFamily: 'ui-monospace, Menlo, monospace' }}
              />
              <Button
                onClick={() => sendNotes(slideNumber)}
                type="primary"
                disabled={!connected}
              >
                Push notes to speaker
              </Button>
              <Alert
                type="info"
                showIcon
                message="Tip: leave this page open on a separate screen while you test the speaker's stage view. The simulator only talks to one slide key at a time."
              />
            </Space>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
